import React, { useState, useRef } from 'react';
import { useController, Control, ControllerProps } from 'react-hook-form';
import { cn } from 'lib/utils';

interface TagInputProps {
  name: string;
  control: Control<any>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  rules?: ControllerProps['rules'];
  allowMultiline?: boolean; // If true, don't split on newlines when pasting
}

const TagInput: React.FC<TagInputProps> = ({
  name,
  control,
  placeholder = 'Add tag...',
  className,
  disabled = false,
  rules,
  allowMultiline = false,
}) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: [],
    rules,
  });

  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = inputValue.trim();
      if (newTag && !value.includes(newTag)) {
        onChange([...value, newTag]);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const removeTag = (indexToRemove: number) => {
    onChange(value.filter((_: string, index: number) => index !== indexToRemove));
  };

  const handleInputBlur = () => {
    const newTag = inputValue.trim();
    if (newTag && !value.includes(newTag)) {
      onChange([...value, newTag]);
      setInputValue('');
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    if (allowMultiline) {
      // For multiline mode (social posts), treat entire paste as one tag
      const trimmedText = pastedText.trim();
      if (trimmedText && !value.includes(trimmedText)) {
        onChange([...value, trimmedText]);
        setInputValue('');
      }
    } else {
      // For normal mode, split by commas and/or newlines
      const newTags = pastedText
        .split(/[,\n]+/)
        .map((tag) => tag.trim())
        .filter((tag) => tag && !value.includes(tag));

      if (newTags.length > 0) {
        onChange([...value, ...newTags]);
        setInputValue('');
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // If user typed a comma, split and add as tag (unless allowMultiline is true)
    if (!allowMultiline && newValue.includes(',')) {
      const parts = newValue
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      if (parts.length > 0) {
        const newTags = parts.filter((tag) => !value.includes(tag));
        if (newTags.length > 0) {
          onChange([...value, ...newTags]);
        }
        setInputValue('');
        return;
      }
    }

    setInputValue(newValue);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div
        className={cn(
          'flex flex-wrap gap-2 p-2 border rounded-md bg-white focus-within:outline-none focus-within:ring-2 focus-within:ring-primary',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag: string, index: number) => (
          <span
            key={index}
            className="inline-flex items-start gap-1 px-3 py-2 bg-[#F4EBFECC] text-black rounded-md text-xs whitespace-pre-wrap"
          >
            <span className="flex-1">{tag}</span>
            <button
              type="button"
              onClick={() => removeTag(index)}
              className="text-black/80 hover:text-black flex-shrink-0"
              disabled={disabled}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          className="flex-1 min-w-[120px] text-sm outline-none bg-transparent text-black placeholder:text-xs placeholder:text-[#989797]"
        />
      </div>
      {error && <p className="text-xs text-destructive">{error.message}</p>}
    </div>
  );
};

export { TagInput };
